import React, { useCallback, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SENSE_MONITOR_MANUFACTURERS } from '../../features/devices/devices-helper';
import { selectCellularModems, selectMeters } from '../site/siteSlice';

export function SenseNotConnectedAlert() {
  const { isOpen, onOpen } = useDisclosure();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const continueRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonBorderColor = useColorModeValue('#D9D9D9', '#494B50');
  const backgroundColor = useColorModeValue('white', '#363E40');
  const meters = useSelector(selectMeters);
  const siteHasCellularModem = !!useSelector(selectCellularModems)?.length;

  // In order to support Sense monitors that may have been migrated, we need to check if any are not connected
  // and alert the user to set them up by directing them to the registration page.
  const senseMetersNotConnected = useMemo(
    () =>
      meters.filter(
        (m) => m.operating_state === 'NOT_CONNECTED' && SENSE_MONITOR_MANUFACTURERS.includes(m.manufacturer_id)
      ),
    [meters]
  );
  const shouldShowAlert = !siteHasCellularModem && !!senseMetersNotConnected.length;

  useEffect(() => {
    if (shouldShowAlert) {
      onOpen();
    }
  }, [shouldShowAlert, onOpen]);

  const handleConfirmSenseSetup = useCallback(() => {
    navigate(
      `../account/hardware/configure/meter/register?serialNumber=${senseMetersNotConnected[0].site_identifier}&direction=forward`
    );
  }, [senseMetersNotConnected, navigate]);

  // Skip displaying the modal if the site has a cellular modem or if the site's monitors are all connected.
  if (!shouldShowAlert) return null;

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={continueRef as React.RefObject<HTMLButtonElement>}
      onClose={() => undefined}
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          top={'220px'}
          bgColor={backgroundColor}
          mx={8}
          rounded={16}
          mt={30}
          overflowX="hidden"
          data-testid="sense-not-connected-alert-dialog-modal"
        >
          <AlertDialogHeader
            textAlign="center"
            pt={6}
            pb={0}
            fontSize="lg"
            fontWeight="bold"
            mx="auto"
            overflowWrap="anywhere"
          >
            {t('Set Up Hardware.monitor setup required title')}
          </AlertDialogHeader>

          <AlertDialogBody pb={4} mx="auto" textAlign="center">
            {t('Set Up Hardware.monitor setup required description')}
          </AlertDialogBody>

          <Flex w="100%" justifyContent={'space-around'}>
            <Button
              data-testid="sense-not-connected-alert-dialog-confirm-btn"
              color={'red.500'}
              w="100%"
              rounded={0}
              py={6}
              variant="ghost"
              whiteSpace={'wrap'}
              ref={continueRef}
              borderTop="1px solid"
              borderColor={buttonBorderColor}
              onClick={() => {
                handleConfirmSenseSetup();
              }}
            >
              {t('Common.continue')}
            </Button>
          </Flex>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
